import { Fragment, useEffect, useRef } from 'react';
import { Link } from 'react-router';
import Badge from '../../components/redesign/Badge';

// A nav entry (`to`) is a real link; an action entry (`onSelect`, e.g. Log out) is a real button
// that runs a callback instead of navigating. `destructive` marks an action item red (Log out),
// matching the desktop UserMenu's treatment of the same action. `to`/`onSelect` are modelled as a
// union — rather than both optional — so an item can't accidentally carry neither. Each member
// also marks the *other* member's field `?: never`: without that, TypeScript's excess-property
// check only rejects a property unknown to *every* member of a union, and `onSelect` is known to
// the second member, so an item carrying both `to` and `onSelect` would compile — then silently
// render as a Link (`typeof item.to === 'string'` wins below) and drop the action. See
// MoreSheetItem.typecheck.ts for the regression test that holds this.
//
// `group` starts a titled section: the first item carrying a group name the item before it did not
// is drawn under a divider and that name (the admin tools use it — Management, Admin).
export type MoreSheetItem =
    | { key: string; label: string; to: string; onSelect?: never; destructive?: boolean; group?: string; badge?: number }
    | { key: string; label: string; to?: never; onSelect: () => void; destructive?: boolean; group?: string; badge?: number };

interface MoreSheetProps {
    open: boolean;
    onClose: () => void;
    items: MoreSheetItem[];
}

function focusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
}

/**
 * The prototype's "More" bottom sheet: everything the tab bar's five slots did not have room for,
 * plus Plan.
 *
 * Carries the prototype's "Switch business" entry as an action item: the topbar chip that opens the
 * business menu is `hidden md:flex`, so it cannot be proxied a click from here — instead
 * RedesignLayout owns a second BusinessMenu, placed for a phone, and this entry opens it. Without
 * it a phone has no way to switch business at all.
 *
 * The sheet stays mounted and off-screen (translateY(101%)) rather than unmounting while closed, to
 * match the prototype's slide transition. That means its links are still in the DOM while "closed",
 * so `aria-hidden` (out of the accessibility tree, so nothing here is announced or reachable by
 * getByRole/screen-reader virtual cursor) and `inert` (out of the tab order and find-in-page, per
 * the same contract BlurGate relies on) both have to do real work here, not just one of them.
 */
export default function MoreSheet({ open, onClose, items }: MoreSheetProps) {
    const sheetRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);
    const titleId = 'mobile-more-sheet-title';

    useEffect(() => {
        if (!open) return;

        // Same focus contract as ContactComposer: capture whatever had focus (the
        // tab bar's More button, in practice) before moving focus into the sheet, and hand it back
        // on close so a keyboard/screen-reader user lands back where they started.
        previouslyFocused.current = document.activeElement as HTMLElement | null;
        sheetRef.current?.focus();

        return () => {
            previouslyFocused.current?.focus();
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }

            if (e.key !== 'Tab' || !sheetRef.current) return;

            // A minimal focus trap: wrap Tab/Shift+Tab at the sheet's edges rather than letting
            // focus escape to whatever the slid-away content underneath happens to hold.
            const focusable = focusableElements(sheetRef.current);
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    const planIndex = items.findIndex((item) => item.key === 'plan');

    return (
        <>
            <div
                data-testid="more-sheet-scrim"
                aria-hidden="true"
                onClick={onClose}
                className={`md:hidden fixed inset-0 z-95 bg-[rgba(20,25,20,.45)] transition-opacity duration-[180ms] ease-[ease] ${
                    open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
            />
            <div
                ref={sheetRef}
                data-testid="more-sheet"
                id="mobile-more-sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-hidden={open ? undefined : true}
                inert={open ? undefined : true}
                tabIndex={-1}
                className={`md:hidden fixed left-0 right-0 bottom-0 z-96 bg-surface rounded-t-[18px] pt-2 pb-[calc(12px+env(safe-area-inset-bottom,0px))] max-h-[80dvh] overflow-auto transition-transform duration-[220ms] ease-[ease] ${
                    open ? 'translate-y-0' : 'translate-y-[101%]'
                }`}
            >
                <h2 id={titleId} className="sr-only">More</h2>
                <div className="w-9 h-1 rounded-full bg-border mt-1.5 mb-2.5 mx-auto" />

                {items.map((item, index) => {
                    // Same destructive treatment as the desktop UserMenu's Log out — see that
                    // component's comment on why this reaches for a raw Tailwind palette colour
                    // rather than a token (the token set has no destructive colour).
                    const itemClassName = `flex items-center gap-3 w-full text-left font-medium text-[15px] px-5 py-[13px] ${
                        item.destructive ? 'text-red-700 dark:text-red-400' : 'text-ink'
                    }`;

                    const startsGroup = item.group !== undefined && item.group !== items[index - 1]?.group;

                    return (
                        <Fragment key={item.key}>
                            {index === planIndex && planIndex > 0 && <hr className="border-0 border-t border-border mx-5 my-2" />}
                            {startsGroup && (
                                <>
                                    <hr className="border-0 border-t border-border mx-5 my-2" />
                                    <div className="px-5 pt-1 pb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                                        {item.group}
                                    </div>
                                </>
                            )}
                            {typeof item.to === 'string' ? (
                                <Link to={item.to} onClick={onClose} className={itemClassName}>
                                    {item.label}
                                    {/* What is new behind it (Saved's new-saves count) — the sidebar's badge, on a phone. */}
                                    {(item.badge ?? 0) > 0 && (
                                        <span className="ml-auto leading-[0]">
                                            <Badge variant="tab-count">{item.badge}</Badge>
                                        </span>
                                    )}
                                </Link>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        onClose();
                                        item.onSelect();
                                    }}
                                    className={itemClassName}
                                >
                                    {item.label}
                                </button>
                            )}
                        </Fragment>
                    );
                })}
            </div>
        </>
    );
}
